// ═══════════════════════════════════════════════════════════════
// PEN TOOL — PathList model, shape generators, path utilities
// ═══════════════════════════════════════════════════════════════
//
// DATA MODEL (v2 — replaces flat point array with _break hack)
//
//   PathList = Path[]
//   Path = { id: string, points: Point[], closed: boolean, name?: string }
//   Point = { x, y, cp1x, cp1y, cp2x, cp2y }
//
// Each Path is an independent sub-path that can be individually
// selected, moved, transformed, stroked, or filled. The old
// `_break` flag on points is gone — sub-path boundaries are now
// explicit Path objects.
//
// Migration: flatToPathList(oldPoints) converts the legacy format.
// ═══════════════════════════════════════════════════════════════

let _nextId = 1;
export function mkPathId() { return "p" + (_nextId++); }

// ── Point constructor ────────────────────────────────────────
export function mkPt(x, y, cp1x, cp1y, cp2x, cp2y) {
  return { x, y, cp1x: cp1x ?? null, cp1y: cp1y ?? null, cp2x: cp2x ?? null, cp2y: cp2y ?? null };
}

// ── Path constructor ─────────────────────────────────────────
export function mkPath(points, closed, name) {
  return { id: mkPathId(), points: points || [], closed: closed || false, name: name || null };
}

// ── Migration: flat Point[] (with _break) → PathList ─────────
export function flatToPathList(pts) {
  if (!pts || pts.length === 0) return [];
  const paths = [];
  let current = [];
  for (let i = 0; i < pts.length; i++) {
    const pt = pts[i];
    if (pt._break && current.length > 0) {
      const closed = current.length >= 3 &&
        current[current.length - 1].x === current[0].x &&
        current[current.length - 1].y === current[0].y;
      paths.push(mkPath(current.map(p => mkPt(p.x, p.y, p.cp1x, p.cp1y, p.cp2x, p.cp2y)), closed));
      current = [];
    }
    current.push(mkPt(pt.x, pt.y, pt.cp1x, pt.cp1y, pt.cp2x, pt.cp2y));
  }
  if (current.length > 0) {
    const closed = current.length >= 3 &&
      current[current.length - 1].x === current[0].x &&
      current[current.length - 1].y === current[0].y;
    paths.push(mkPath(current.map(p => mkPt(p.x, p.y, p.cp1x, p.cp1y, p.cp2x, p.cp2y)), closed));
  }
  return paths;
}

// ── PathList → flat Point[] (for engine commands that still expect flat) ──
export function pathListToFlat(pathList) {
  const pts = [];
  for (let pi = 0; pi < pathList.length; pi++) {
    const path = pathList[pi];
    for (let i = 0; i < path.points.length; i++) {
      const pt = { ...path.points[i] };
      if (pi > 0 && i === 0) pt._break = true;
      pts.push(pt);
    }
  }
  return pts;
}

// ── Bounding box for a single Path ───────────────────────────
export function pathBounds(path) {
  if (!path || !path.points || path.points.length === 0) return null;
  return pointsBounds(path.points);
}

// ── Bounding box for a set of points ─────────────────────────
export function pointsBounds(pts) {
  if (!pts || pts.length === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const pt of pts) {
    minX = Math.min(minX, pt.x); minY = Math.min(minY, pt.y);
    maxX = Math.max(maxX, pt.x); maxY = Math.max(maxY, pt.y);
    if (pt.cp1x != null) { minX = Math.min(minX, pt.cp1x); minY = Math.min(minY, pt.cp1y); maxX = Math.max(maxX, pt.cp1x); maxY = Math.max(maxY, pt.cp1y); }
    if (pt.cp2x != null) { minX = Math.min(minX, pt.cp2x); minY = Math.min(minY, pt.cp2y); maxX = Math.max(maxX, pt.cp2x); maxY = Math.max(maxY, pt.cp2y); }
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY, cx: minX + (maxX - minX) / 2, cy: minY + (maxY - minY) / 2 };
}

// ── Bounding box for the entire PathList ─────────────────────
export function pathListBounds(pathList) {
  if (!pathList || pathList.length === 0) return null;
  const all = [];
  for (const path of pathList) {
    for (const pt of path.points) all.push(pt);
  }
  return pointsBounds(all);
}

// ── LEGACY COMPAT: penBounds works on either format ──────────
export function penBounds(input) {
  if (!input || input.length === 0) return null;
  if (input[0] && Array.isArray(input[0].points)) {
    return pathListBounds(input);
  }
  return pointsBounds(input);
}

// ── Apply transform to points ────────────────────────────────
export function applyTransformToPoints(pts, fn) {
  return pts.map(pt => {
    const np = fn(pt.x, pt.y);
    const r = { ...pt, x: np.x, y: np.y };
    if (pt.cp1x != null) { const c1 = fn(pt.cp1x, pt.cp1y); r.cp1x = c1.x; r.cp1y = c1.y; }
    if (pt.cp2x != null) { const c2 = fn(pt.cp2x, pt.cp2y); r.cp2x = c2.x; r.cp2y = c2.y; }
    return r;
  });
}

// ── Apply transform to a single Path ─────────────────────────
export function transformPath(path, fn) {
  return { ...path, points: applyTransformToPoints(path.points, fn) };
}

// ── Apply transform to entire PathList ───────────────────────
export function transformPathList(pathList, fn) {
  return pathList.map(p => transformPath(p, fn));
}

// ── LEGACY COMPAT: penApplyTransform works on either format ──
export function penApplyTransform(input, fn) {
  if (!input || input.length === 0) return input;
  if (input[0] && Array.isArray(input[0].points)) {
    return transformPathList(input, fn);
  }
  return applyTransformToPoints(input, fn);
}

// ── Total point count across PathList ────────────────────────
export function totalPointCount(pathList) {
  let n = 0;
  for (const p of pathList) n += p.points.length;
  return n;
}

// ── Find which path+point an (x,y) is near ──────────────────
// Returns { pathIdx, pointIdx, handle } or null.
// handle: "anchor" | "cp1" | "cp2"
export function hitTestPoints(pathList, px, py, hitR) {
  hitR = hitR || 8;
  for (let pi = 0; pi < pathList.length; pi++) {
    const pts = pathList[pi].points;
    for (let i = 0; i < pts.length; i++) {
      const pt = pts[i];
      if (pt.cp2x != null && Math.abs(px - pt.cp2x) < hitR && Math.abs(py - pt.cp2y) < hitR) {
        return { pathIdx: pi, pointIdx: i, handle: "cp2" };
      }
      if (pt.cp1x != null && Math.abs(px - pt.cp1x) < hitR && Math.abs(py - pt.cp1y) < hitR) {
        return { pathIdx: pi, pointIdx: i, handle: "cp1" };
      }
      if (Math.abs(px - pt.x) < hitR && Math.abs(py - pt.y) < hitR) {
        return { pathIdx: pi, pointIdx: i, handle: "anchor" };
      }
    }
  }
  return null;
}

// ── Check if (x,y) is inside a path's bounding box ──────────
// Returns pathIdx or -1
export function hitTestPathBBox(pathList, px, py, pad) {
  pad = pad || 4;
  for (let pi = 0; pi < pathList.length; pi++) {
    const b = pathBounds(pathList[pi]);
    if (!b || b.w < 1 || b.h < 1) continue;
    if (px >= b.x - pad && px <= b.x + b.w + pad && py >= b.y - pad && py <= b.y + b.h + pad) {
      return pi;
    }
  }
  return -1;
}

// ── Check if clicking near first point of a path (for closing) ──
export function isNearFirstPoint(path, px, py, threshold) {
  threshold = threshold || 20;
  if (!path || path.points.length < 3 || path.closed) return false;
  const fp = path.points[0];
  return Math.sqrt((px - fp.x) ** 2 + (py - fp.y) ** 2) < threshold;
}

// ── Close a path by appending a copy of its first point ──────
export function closePath(path) {
  if (path.closed || path.points.length < 3) return path;
  return { ...path, points: [...path.points, mkPt(path.points[0].x, path.points[0].y)], closed: true };
}

// ── Update a single point within a PathList (immutable) ──────
export function updatePoint(pathList, pathIdx, pointIdx, updater) {
  return pathList.map((p, pi) => {
    if (pi !== pathIdx) return p;
    return { ...p, points: p.points.map((pt, i) => i === pointIdx ? updater(pt) : pt) };
  });
}

// ── Add a point to the end of a specific path ────────────────
export function appendPoint(pathList, pathIdx, point) {
  return pathList.map((p, pi) => {
    if (pi !== pathIdx) return p;
    return { ...p, points: [...p.points, point] };
  });
}

// ── Remove a point from a path ───────────────────────────────
export function removePoint(pathList, pathIdx, pointIdx) {
  return pathList.map((p, pi) => {
    if (pi !== pathIdx) return p;
    const newPts = p.points.filter((_, i) => i !== pointIdx);
    return { ...p, points: newPts };
  });
}

// ── Delete an entire path from the list ──────────────────────
export function deletePath(pathList, pathIdx) {
  return pathList.filter((_, i) => i !== pathIdx);
}

// ═══════════════════════════════════════════════════════════════
// Shape generators — return a Path object
// ═══════════════════════════════════════════════════════════════

export function genShape(type, bx, by, bw, bh) {
  const cx = bx + bw / 2, cy = by + bh / 2, rx = Math.abs(bw) / 2, ry = Math.abs(bh) / 2;

  const polygon = (n, rot = 0) => {
    const pts = [];
    for (let i = 0; i <= n; i++) {
      const a = rot + i * (Math.PI * 2 / n) - Math.PI / 2;
      pts.push(mkPt(cx + Math.cos(a) * rx, cy + Math.sin(a) * ry));
    }
    return mkPath(pts, true, type);
  };

  if (type === "triangle") return polygon(3);
  if (type === "pentagon") return polygon(5);
  if (type === "hexagon") return polygon(6);

  if (type === "star") {
    const pts = []; const n = 5;
    for (let i = 0; i <= n * 2; i++) {
      const a = -Math.PI / 2 + i * (Math.PI / n);
      const f = i % 2 === 0 ? 1 : 0.38;
      pts.push(mkPt(cx + Math.cos(a) * rx * f, cy + Math.sin(a) * ry * f));
    }
    return mkPath(pts, true, "star");
  }

  if (type === "burst") {
    const pts = []; const n = 12;
    for (let i = 0; i <= n * 2; i++) {
      const a = -Math.PI / 2 + i * (Math.PI / n);
      const f = i % 2 === 0 ? 1 : 0.6;
      pts.push(mkPt(cx + Math.cos(a) * rx * f, cy + Math.sin(a) * ry * f));
    }
    return mkPath(pts, true, "burst");
  }

  if (type === "arrow") {
    const aw = rx * 0.3;
    return mkPath([
      mkPt(cx, cy - ry), mkPt(cx + rx * 0.6, cy), mkPt(cx + aw, cy), mkPt(cx + aw, cy + ry),
      mkPt(cx - aw, cy + ry), mkPt(cx - aw, cy), mkPt(cx - rx * 0.6, cy), mkPt(cx, cy - ry)
    ], true, "arrow");
  }

  if (type === "heart") {
    return mkPath([
      { x: cx, y: cy + ry * 0.9, cp1x: null, cp1y: null, cp2x: cx - rx * 0.2, cp2y: cy + ry * 0.9 },
      { x: cx - rx, y: cy - ry * 0.2, cp1x: cx - rx * 1.1, cp1y: cy + ry * 0.5, cp2x: cx - rx * 0.9, cp2y: cy - ry * 0.7 },
      { x: cx, y: cy - ry * 0.3, cp1x: cx - rx * 0.3, cp1y: cy - ry * 0.9, cp2x: cx + rx * 0.3, cp2y: cy - ry * 0.9 },
      { x: cx + rx, y: cy - ry * 0.2, cp1x: cx + rx * 0.9, cp1y: cy - ry * 0.7, cp2x: cx + rx * 1.1, cp2y: cy + ry * 0.5 },
      { x: cx, y: cy + ry * 0.9, cp1x: cx + rx * 0.2, cp1y: cy + ry * 0.9, cp2x: null, cp2y: null },
    ], true, "heart");
  }

  if (type === "circle") {
    const k = 0.5522847498;
    return mkPath([
      { x: cx, y: cy - ry, cp1x: cx + rx * k, cp1y: cy - ry, cp2x: cx + rx * k, cp2y: cy - ry },
      { x: cx + rx, y: cy, cp1x: cx + rx, cp1y: cy - ry * k, cp2x: cx + rx, cp2y: cy + ry * k },
      { x: cx, y: cy + ry, cp1x: cx + rx * k, cp1y: cy + ry, cp2x: cx - rx * k, cp2y: cy + ry },
      { x: cx - rx, y: cy, cp1x: cx - rx, cp1y: cy + ry * k, cp2x: cx - rx, cp2y: cy - ry * k },
      { x: cx, y: cy - ry, cp1x: cx - rx * k, cp1y: cy - ry, cp2x: null, cp2y: null },
    ], true, "circle");
  }

  return mkPath([], false);
}

// ═══════════════════════════════════════════════════════════════
// Drawing — render paths on a canvas 2D context (for overlays)
// ═══════════════════════════════════════════════════════════════

// Draw a single path's bezier curve onto ctx (does NOT call beginPath)
export function drawPathCurve(ctx, path) {
  if (!path || path.points.length < 2) return;
  const pts = path.points;
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1], cur = pts[i];
    const c1x = prev.cp2x != null ? prev.cp2x : prev.x;
    const c1y = prev.cp2y != null ? prev.cp2y : prev.y;
    const c2x = cur.cp1x != null ? cur.cp1x : cur.x;
    const c2y = cur.cp1y != null ? cur.cp1y : cur.y;
    ctx.bezierCurveTo(c1x, c1y, c2x, c2y, cur.x, cur.y);
  }
  if (path.closed) ctx.closePath();
}

// Draw the entire PathList (for engine PEN_TO_SEL / PEN_STROKE)
// Legacy compat: also accepts flat point array with _break flags
export function drawPenPath(ctx, pts) {
  if (!pts || pts.length === 0) return;
  // Detect flat point array (legacy)
  if (pts.length > 0 && !Array.isArray(pts[0]?.points)) {
    if (pts.length < 2) return;
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1], cur = pts[i];
      if (cur._break) { ctx.moveTo(cur.x, cur.y); continue; }
      const c1x = prev.cp2x != null ? prev.cp2x : prev.x;
      const c1y = prev.cp2y != null ? prev.cp2y : prev.y;
      const c2x = cur.cp1x != null ? cur.cp1x : cur.x;
      const c2y = cur.cp1y != null ? cur.cp1y : cur.y;
      ctx.bezierCurveTo(c1x, c1y, c2x, c2y, cur.x, cur.y);
    }
    return;
  }
  // New PathList format
  ctx.beginPath();
  for (const path of pts) {
    drawPathCurve(ctx, path);
  }
}

// ═══════════════════════════════════════════════════════════════
// Bezier segment splitting — add anchor point on curve
// ═══════════════════════════════════════════════════════════════

// Evaluate cubic bezier at parameter t (0..1)
function cubicAt(p0x, p0y, c1x, c1y, c2x, c2y, p1x, p1y, t) {
  const u = 1 - t;
  const x = u*u*u*p0x + 3*u*u*t*c1x + 3*u*t*t*c2x + t*t*t*p1x;
  const y = u*u*u*p0y + 3*u*u*t*c1y + 3*u*t*t*c2y + t*t*t*p1y;
  return { x, y };
}

// Find the nearest point on a cubic bezier segment to (px, py)
// Returns { t, dist } where t is the parameter and dist is the distance
function nearestOnCubic(p0x, p0y, c1x, c1y, c2x, c2y, p1x, p1y, px, py) {
  let bestT = 0, bestDist = Infinity;
  // Coarse scan with 20 samples, then refine
  const steps = 20;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const pt = cubicAt(p0x, p0y, c1x, c1y, c2x, c2y, p1x, p1y, t);
    const d = (pt.x - px) ** 2 + (pt.y - py) ** 2;
    if (d < bestDist) { bestDist = d; bestT = t; }
  }
  // Refine with 10 steps in the best region
  const dt = 1 / steps;
  let lo = Math.max(0, bestT - dt), hi = Math.min(1, bestT + dt);
  for (let i = 0; i <= 10; i++) {
    const t = lo + (hi - lo) * i / 10;
    const pt = cubicAt(p0x, p0y, c1x, c1y, c2x, c2y, p1x, p1y, t);
    const d = (pt.x - px) ** 2 + (pt.y - py) ** 2;
    if (d < bestDist) { bestDist = d; bestT = t; }
  }
  return { t: bestT, dist: Math.sqrt(bestDist) };
}

// Hit-test all segments across all paths. Returns { pathIdx, segIdx, t, dist } or null.
// segIdx is the index of the END point of the segment (segment goes from points[segIdx-1] to points[segIdx])
export function hitTestSegment(pathList, px, py, threshold) {
  threshold = threshold || 8;
  let best = null;
  for (let pi = 0; pi < pathList.length; pi++) {
    const pts = pathList[pi].points;
    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1], cur = pts[i];
      const c1x = prev.cp2x != null ? prev.cp2x : prev.x;
      const c1y = prev.cp2y != null ? prev.cp2y : prev.y;
      const c2x = cur.cp1x != null ? cur.cp1x : cur.x;
      const c2y = cur.cp1y != null ? cur.cp1y : cur.y;
      const hit = nearestOnCubic(prev.x, prev.y, c1x, c1y, c2x, c2y, cur.x, cur.y, px, py);
      if (hit.dist < threshold && (!best || hit.dist < best.dist)) {
        best = { pathIdx: pi, segIdx: i, t: hit.t, dist: hit.dist };
      }
    }
  }
  return best;
}

// Split a bezier segment at parameter t using De Casteljau subdivision.
// Inserts a new anchor point with correct control handles.
// Returns a new PathList with the point inserted.
export function splitSegmentAt(pathList, pathIdx, segIdx, t) {
  return pathList.map((pa, pi) => {
    if (pi !== pathIdx) return pa;
    const pts = pa.points;
    const prev = pts[segIdx - 1], cur = pts[segIdx];
    const p0x = prev.x, p0y = prev.y;
    const c1x = prev.cp2x != null ? prev.cp2x : prev.x;
    const c1y = prev.cp2y != null ? prev.cp2y : prev.y;
    const c2x = cur.cp1x != null ? cur.cp1x : cur.x;
    const c2y = cur.cp1y != null ? cur.cp1y : cur.y;
    const p1x = cur.x, p1y = cur.y;

    // De Casteljau split
    const u = 1 - t;
    // First level
    const a1x = u*p0x + t*c1x, a1y = u*p0y + t*c1y;
    const a2x = u*c1x + t*c2x, a2y = u*c1y + t*c2y;
    const a3x = u*c2x + t*p1x, a3y = u*c2y + t*p1y;
    // Second level
    const b1x = u*a1x + t*a2x, b1y = u*a1y + t*a2y;
    const b2x = u*a2x + t*a3x, b2y = u*a2y + t*a3y;
    // Third level — the split point
    const mx = u*b1x + t*b2x, my = u*b1y + t*b2y;

    // New prev: cp2 becomes a1 (first half's second control)
    const newPrev = { ...prev, cp2x: a1x, cp2y: a1y };
    // New mid point: cp1=b1, cp2=b2
    const newMid = mkPt(mx, my, b1x, b1y, b2x, b2y);
    // New cur: cp1 becomes a3 (second half's first control)
    const newCur = { ...cur, cp1x: a3x, cp1y: a3y };

    const newPts = [...pts];
    newPts[segIdx - 1] = newPrev;
    newPts[segIdx] = newCur;
    newPts.splice(segIdx, 0, newMid);
    return { ...pa, points: newPts };
  });
}

// ═══════════════════════════════════════════════════════════════
// Boolean path operations — union, subtract, intersect
// ═══════════════════════════════════════════════════════════════
// Strategy: rasterize paths to mask canvases, combine with Canvas2D
// composite operations, then trace the result boundary back to points.

// Rasterize a single Path to an alpha mask on an OffscreenCanvas
function rasterizePath(path, w, h) {
  const c = new OffscreenCanvas(w, h);
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  drawPathCurve(ctx, path);
  ctx.closePath();
  ctx.fill();
  return c;
}

// Trace the boundary of a binary mask to a point array (Moore neighborhood)
function traceMask(mask, w, h, simplifyN) {
  simplifyN = simplifyN || 200;
  // Find first edge pixel
  let startX = -1, startY = -1;
  outer: for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (mask[y * w + x] >= 128) {
      const above = y > 0 ? mask[(y - 1) * w + x] < 128 : true;
      if (above) { startX = x; startY = y; break outer; }
    }
  }
  if (startX < 0) return [];
  const dirs = [[0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1]];
  let cx = startX, cy = startY, dir = 0, steps = 0;
  const raw = [];
  const maxSteps = Math.max(w * h, 50000);
  do {
    if (steps % 3 === 0) raw.push(mkPt(cx, cy));
    let found = false;
    for (let d = 0; d < 8; d++) {
      const nd = (dir + 5 + d) % 8;
      const nx = cx + dirs[nd][0], ny = cy + dirs[nd][1];
      if (nx >= 0 && nx < w && ny >= 0 && ny < h && mask[ny * w + nx] >= 128) {
        cx = nx; cy = ny; dir = nd; found = true; break;
      }
    }
    if (!found) break;
    steps++;
  } while ((cx !== startX || cy !== startY) && steps < maxSteps);
  // Simplify
  const step = Math.max(1, Math.floor(raw.length / simplifyN));
  const simplified = raw.filter((_, i) => i % step === 0);
  if (simplified.length >= 2) simplified.push(mkPt(simplified[0].x, simplified[0].y));
  return simplified;
}

// Get mask (Uint8Array of alpha values) from a canvas
function canvasToMask(canvas) {
  const ctx = canvas.getContext("2d");
  const id = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const mask = new Uint8Array(canvas.width * canvas.height);
  for (let i = 0; i < mask.length; i++) mask[i] = id.data[i * 4 + 3];
  return mask;
}

/**
 * Boolean operation between two paths.
 * op: "union" | "subtract" | "intersect"
 * canvasW, canvasH: dimensions of the document (for rasterization)
 * Returns a new Path (or null if result is empty).
 */
export function booleanPathOp(pathA, pathB, op, canvasW, canvasH) {
  const w = canvasW || 2048, h = canvasH || 2048;
  const cA = rasterizePath(pathA, w, h);
  const cB = rasterizePath(pathB, w, h);
  // Combine
  const result = new OffscreenCanvas(w, h);
  const ctx = result.getContext("2d");
  if (op === "union") {
    ctx.drawImage(cA, 0, 0);
    ctx.drawImage(cB, 0, 0);
  } else if (op === "subtract") {
    ctx.drawImage(cA, 0, 0);
    ctx.globalCompositeOperation = "destination-out";
    ctx.drawImage(cB, 0, 0);
  } else if (op === "intersect") {
    ctx.drawImage(cA, 0, 0);
    ctx.globalCompositeOperation = "destination-in";
    ctx.drawImage(cB, 0, 0);
  }
  const mask = canvasToMask(result);
  const pts = traceMask(mask, w, h);
  if (pts.length < 3) return null;
  return mkPath(pts, true, `${pathA.name || "A"} ${op} ${pathB.name || "B"}`);
}

/**
 * Apply a boolean operation between the active path and another path in the list.
 * Returns a new PathList with the result replacing both operands.
 */
export function applyBooleanOp(pathList, activeIdx, targetIdx, op, canvasW, canvasH) {
  const pathA = pathList[activeIdx];
  const pathB = pathList[targetIdx];
  if (!pathA || !pathB) return pathList;
  const result = booleanPathOp(pathA, pathB, op, canvasW, canvasH);
  if (!result) {
    // Empty result — remove both
    return pathList.filter((_, i) => i !== activeIdx && i !== targetIdx);
  }
  // Replace the lower index, remove the higher
  const lo = Math.min(activeIdx, targetIdx);
  const hi = Math.max(activeIdx, targetIdx);
  return pathList
    .map((p, i) => i === lo ? result : p)
    .filter((_, i) => i !== hi);
}
